import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Typography,
	Box,
	InputBase,
	Tooltip,
	FormHelperText,
	// Chip,
	Grid,
} from '@mui/material';
import { listLiteTopology } from '../../actions/groupActions';
import Checkbox from '../checkbox/Checkbox';

import '../multipeSelect/MultiSelect.scss';
import useTheme from '../../hooks/useTheme';
import {
	ArrowDownIcon,
	PurpleCrossIcon,
	ArrowUpIcon,
	CheckIcon,
} from '../../assets/icons';
import './TopologySelect.scss';
import Loader from '../loader/Loader';
import Label from '../label/Label';
import { FixedSizeList } from 'react-window';
const ROW_HEIGHT = 33;
const MAX_VISIBLE_ITEMS = 6;

/** Custom disabled cursor (same pattern as CustomCheckbox), with not-allowed fallback. */
const TOPOLOGY_DISABLED_CURSOR =
	'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\'><text x=\'0\' y=\'20\' font-size=\'20\' fill=\'red\'>🚫</text></svg>"), not-allowed';

const ROOT_SITE_PARENT_ID = 'b8038fc5-d0fc-438b-ba54-df868280b8c2';
let liteTopologyInFlight = false;
const TopologySelectWOLazyLoading = ({
	isMultiSelect = false,
	onChangeSelection,
	label = 'Select Parent Site',
	width = 280,
	selectedItems = [],
	selectAllRequired = false,
	error,
	required,
	isDisabled = false,
	readOnly = false,
	sx,
	onSelectAllDb,
	isTipSitesListed = true,
	isTip,
	setSlctAll = ()=>{},
	excludeSiteIds = [],
	showRootSitesOnly = false,
	rootSitesOnlyValue = false,
	onRootSitesOnlyChange,
	onRootSitesOnlyClick,
	onCloseDropdown,
}) => {
	const dispatch = useDispatch();
	const { liteSites: allSites, liteLoading } = useSelector(
		(state) => state.groupList
	); // Renamed to allSites to distinguish from filtered
	const { color, FontFace, spacing } = useTheme();
	const [selectedSites, setSelectedSites] = useState(selectedItems || []);
	const [searchTerm, setSearchTerm] = useState('');
	const [rootSitesOnly, setRootSitesOnly] = useState(false);
	// Keep internal checkbox state in sync with parent-controlled value.
	useEffect(() => {
		setRootSitesOnly(!!rootSitesOnlyValue);
	}, [rootSitesOnlyValue]);


	const [open, setOpen] = useState(false);
	const [filteredSites, setFilteredSites] = useState([]); // New state for filtered sites
	const selectRef = useRef(null);
	const selectedSitesList = Array.isArray(selectedSites) ? selectedSites : [];
	
	useEffect(() => {
		if (!selectedItems) {
			setSelectedSites(isMultiSelect ? [] : null);
			return;
		}
	}, [selectedItems, isMultiSelect]);

	useEffect(() => {
		const handleCloseAll = () => {
			setOpen(false);
			selectRef.current?.querySelector('input')?.blur();
		};

		window.addEventListener('close-all-dropdowns', handleCloseAll);

		return () => {
			window.removeEventListener('close-all-dropdowns', handleCloseAll);
		};
	}, []);

	useEffect(() => {
		if (!allSites || !selectedItems) return;

		if (isMultiSelect && Array.isArray(selectedItems)) {
			// Multi-select: map selectedItems to full objects
			const updatedSelectedSites = selectedItems
				.map((selected) => {
					const fullSite = allSites.find(
						(site) => site.site_id === selected.site_id
					);
					return fullSite || selected;
				})
				.filter(Boolean);
			setSelectedSites(updatedSelectedSites);
		} else {
			const singleSelected =
				Array.isArray(selectedItems) && selectedItems.length > 0
					? selectedItems[0]
					: selectedItems;
			// Single-select: find the single matching site or use the passed object
			const fullSite = allSites.find(
				(site) => site.site_id === singleSelected?.site_id
			);
			setSelectedSites(fullSite || selectedItems || null);
		}
	}, [allSites, selectedItems, isMultiSelect]);
	// Expose close function to parent
	useEffect(() => {
		if (onCloseDropdown) {
			onCloseDropdown(() => {
				setOpen(false);
				selectRef.current?.querySelector('input')?.blur();
			});
		}
	}, [onCloseDropdown]);

	// Handle clicks outside the component
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				selectRef.current &&
				!selectRef.current.contains(event.target) &&
				!event.target.closest('.MuiMenu-paper')
			) {
				setOpen(false);
				selectRef.current.querySelector('input')?.blur();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Fetch all sites once on mount (no search term in API call)
	useEffect(() => {
		if (typeof isTip === 'undefined') return;
		const loadSites = async () => {
			if (liteTopologyInFlight) return;

			liteTopologyInFlight = true;
			try {
				await dispatch(
					listLiteTopology(
						isTip === false ? 0 : isTip === true ? 1 : null
					)
				); // Load all sites initially
			} catch (error) {
				console.error('Error fetching sites:', error);
			} finally {
				liteTopologyInFlight = false;
			}
		};

		loadSites();
	}, [dispatch, isTip]);

// Memoize excludeSiteIds to prevent unnecessary re-renders
    const excludeSiteIdsString = JSON.stringify(excludeSiteIds);
    
    // Filter sites based on search term and root sites only checkbox
    useEffect(() => {
        if (!allSites) return;
        let filtered = allSites.filter((site) =>
            (site?.nested_site_name || site?.site_name)
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
        );
        
        // Filter by root sites only if checkbox is checked
        if (rootSitesOnly) {
            filtered = filtered.filter(
                (site) => site?.parent_id === ROOT_SITE_PARENT_ID
            );
        }
        
        const visibleSites = filtered.filter(
            site =>
                !excludeSiteIds.includes(site.site_id) ||
                selectedSitesList.some(s => s.site_id === site.site_id)
            );
        setFilteredSites(isTipSitesListed ? visibleSites : visibleSites.filter(e => !e.is_tip_enabled));
        // selectedSites is removed from dependencies - it's only used in filter logic, not as a trigger
        // excludeSiteIdsString is used instead of excludeSiteIds to compare by value, not reference
    }, [allSites, searchTerm, isTipSitesListed, rootSitesOnly, excludeSiteIdsString]);

	const handleSelectAll = (checked) => {
		if (checked) {
			setSlctAll(true);
			const mergedItems = [];
			filteredSites.forEach((site) => {
				if (
					!mergedItems.some((item) => item?.site_id === site?.site_id)
				) {
					mergedItems.push(site);
				}
			});
			setSelectedSites(mergedItems);
			onChangeSelection && onChangeSelection(mergedItems);
			onSelectAllDb &&
				onSelectAllDb(mergedItems.length === allSites.length);
		} else {
			setSlctAll(false)
			const filteredItems = selectedItems.filter(
				(item) =>
					!filteredSites.some(
						(site) => site?.site_id === item?.site_id
					)
			);
			setSelectedSites(filteredItems);
			onChangeSelection && onChangeSelection(filteredItems);
			onSelectAllDb &&
				onSelectAllDb(filteredItems.length === allSites.length);
		}
	};

	// const handleChange = (event) => {
	// 	console.log('handle change values are', event.target.value);

	// 	if (!event || !event.target || !event.target.value) return;
	// 	const value = event.target.value;

	// 	if (Array.isArray(value)) {
	// 		const filteredValues = value.filter((item) => item !== undefined);
	// 		setSelectedSites(filteredValues);
	// 		onChangeSelection && onChangeSelection(filteredValues);
	// 	} else if (value !== undefined) {
	// 		setSelectedSites(value);
	// 		onChangeSelection && onChangeSelection(value);
	// 	}
	// };
	useEffect(()=>{
		if(selectedSites?.length === filteredSites?.length){
			setSlctAll(true);
		}else if(selectedSites?.length !== filteredSites?.length){
			setSlctAll(false);
		}
	},[selectedSites])
	const handleChange = (event) => {
		const value = event?.target?.value;
		// This should now log correctly

		if (isMultiSelect) {
			// Multi-select: value is an array
			const filteredValues = value.filter((item) => item !== undefined);
			setSelectedSites(filteredValues);
			onChangeSelection && onChangeSelection(filteredValues);
			onSelectAllDb &&
				onSelectAllDb(filteredValues?.length === allSites?.length);
		} else {
			// Single-select: value is a single object
			setSelectedSites(value || null); // Set to null if no value
			onChangeSelection && onChangeSelection(value || null);
		}
	};
	const handleDelete = (itemToRemove, event) => {
		if (isMultiSelect) {
			const updatedItems = selectedItems.filter(
				(item) => item?.site_id !== itemToRemove?.site_id
			);
			onChangeSelection && onChangeSelection(updatedItems);
			onSelectAllDb &&
				onSelectAllDb(updatedItems?.length === allSites?.length);
		} else {
			onChangeSelection && onChangeSelection(null);
		}
	};
	const renderRow = ({ index, style }) => {
		const site = filteredSites[index];
		const handleItemClick = () => {
			if (isMultiSelect) {
				// Multi-select: Toggle the item in the selectedSites array
				const isSelected = selectedSitesList.some(
					(selectedSite) => selectedSite?.site_id === site?.site_id
				);
				const updatedValues = isSelected
					? selectedSites.filter(
							(selectedSite) =>
								selectedSite?.site_id !== site?.site_id
					  )
					: [...selectedSitesList, site];
				setSelectedSites(updatedValues);
				onChangeSelection && onChangeSelection(updatedValues);
				// Simulate the event for Select's onChange
				handleChange({ target: { value: updatedValues } });
			} else {
				if (selectedSites?.site_id === site?.site_id) {
					setSelectedSites({}); // Unselect the item
					onChangeSelection && onChangeSelection({}); // Notify parent
				} else {
					setSelectedSites(site); // Set the single site
					onChangeSelection && onChangeSelection(site); // Notify parent
				}
			}
		};
		return (
			<MenuItem
				key={site?.site_id}
				value={site}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
					width: width,
					// maxWidth: width,
					height: '33px',
					fontSize: FontFace.font_size.input_text,
					color: color.text.primary,
					fontFamily: 'Inter-Regular',
					padding: spacing.base_unit_4,
					backgroundColor:
						isMultiSelect && Array.isArray(selectedItems)
							? selectedItems?.some(
									(selectedSite) =>
										selectedSite?.site_id === site?.site_id
							  )
								? color.background.brand_outline
								: color.background.primary
							: selectedItems?.site_id === site?.site_id
							? color.background.brand_outline
							: color.background.primary,
					...style,
				}}
				onClick={handleItemClick}
			>
				{isMultiSelect ? (
					<Checkbox
						dataTestId="multipleSelect-Checkbox"
						sx={{
							marginRight: '4px',
							minWidth: '14px',
							height: '14px',
						}}
						checked={
							isMultiSelect &&
							Array.isArray(selectedItems) &&
							selectedSites?.some(
								(selectedSite) =>
									selectedSite?.site_id === site?.site_id
							)
						}
						onChecked={(checked) => {
							const updatedValues = checked
								? [...selectedItems, site]
								: selectedItems?.filter(
										(value) =>
											value?.site_id !== site?.site_id
								  );
							setSelectedSites(updatedValues);
							if (onChangeSelection) {
								onChangeSelection(updatedValues);
								onSelectAllDb &&
									onSelectAllDb(
										updatedValues?.length === allSites?.length
									);
							}
						}}
					/>
				) : (
					<></>
				)}
				<Tooltip
					title={site?.nested_site_name || site?.site_name}
					PopperProps={{ disablePortal: true }}
					componentsProps={{
						tooltip: {
							sx: {
								bottom: '-4px',
								bgcolor: '#ffffff',
								color: '#000000',
								// border: '1px solid rgba(0, 0, 0, 0.5)',
								// borderRadius: '4px',
								padding: '4px 8px',
								// boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.16)',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
								maxWidth: width,
								whiteSpace: 'pre-wrap',   // allows multiline text
        						wordBreak: 'break-word',  // breaks long words
        						overflowWrap: 'anywhere',
								whiteSpace: 'normal',
								wordWrap: 'break-word',
								textAlign: 'center',
								boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.16)',
								border: 'solid 1px #dedede',
								backgroundColor: '#fff',
								color: '#000',
								fontFamily: 'Inter-Regular',
								fontSize: '12px',
								fontWeight: 'normal',
							},
						},
					}}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '100%',
						}}
					>
						<span
							style={{
								flex: 1,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{site?.nested_site_name || site?.site_name}
						</span>
						{!isMultiSelect &&
							selectedSites?.site_id === site.site_id && (
								<CheckIcon width={'16px'} height={'16px'} />
							)}
					</div>
				</Tooltip>
			</MenuItem>
		);
	};

	return (
		<div ref={selectRef} style={sx}>
			<FormControl
				variant="standard"
				sx={{
					height: '48px',
					width: sx ? null : width,
					flex: 1,
					...(isDisabled && {
						cursor: TOPOLOGY_DISABLED_CURSOR,
						'& .MuiInputBase-root': { cursor: TOPOLOGY_DISABLED_CURSOR },
						'& .MuiSelect-select': { cursor: TOPOLOGY_DISABLED_CURSOR },
						'& .MuiInputLabel-root': { cursor: TOPOLOGY_DISABLED_CURSOR },
						'& .MuiInput-underline:after': {
							borderBottom: 'none',
						},
						...sx,
					}),
				}}
			>
				<InputLabel
					style={{
						fontFamily: 'Inter-Regular',
						fontSize: open ||
							(isMultiSelect
								? (selectedSites?.length > 0)
								: Boolean(selectedSites && Object.keys(selectedSites).length > 0))
								? FontFace.font_size.xx_medium
								: FontFace.font_size.input_text,
						color: color.text.secondary,
					}}
					shrink={
						open ||
						Object.values(selectedItems || {}).some(val => val !== undefined)
					}
				>
					{label}
				</InputLabel>
				<Select
					disabled={isDisabled}
					onOpen={(e) => {
					  if (isDisabled) {
					    e.preventDefault();
					    return;
					  }
					
					  window.dispatchEvent(new Event('close-all-dropdowns'));
					
					  setOpen(true);
					}}
					onClose={() => setOpen(false)}
					open={isDisabled ? false : open}
					required={required}
					multiple={isMultiSelect}
					IconComponent={
						!isDisabled
							? () => (
									<div
										onClick={(e) => {
										  if (isDisabled) return;
																				
										  e.stopPropagation();
																				
										  if (!open) {
										    window.dispatchEvent(new Event('close-all-dropdowns'));
										  }
									  
										  setOpen(!open);
										}}
										style={{
											cursor: 'pointer',
											opacity: 1,
											display: 'flex',
											alignItems: 'center',
											position: 'absolute',
											right: '8px',
											top: '50%',
											transform: 'translateY(-50%)',
										}}
									>
										{open ? (
											<ArrowUpIcon
												width={'24px'}
												height={'24px'}
												style={{
													cursor: 'pointer',
													stroke: color.icon.brand,
												}}
											/>
										) : (
											<ArrowDownIcon
												width={'24px'}
												height={'24px'}
												style={{
													cursor: 'pointer',
													color: color.icon.secondary,
												}}
											/>
										)}
									</div>
							  )
							: null
					}
					// value={
					// 	selectedSites &&
					// 	(selectedSites?.length > 0 ||
					// 		Object.keys(selectedSites).length > 0)
					// 		? selectedSites
					// 		: []
					// }
					value={
						isMultiSelect
							? selectedSites?.length > 0
								? selectedSites
								: []
							: selectedSites || null
					}
					onChange={handleChange}
					label={label}
					error={!!error}
					MenuProps={{
						PaperProps: {
							style: {
								maxHeight: 300,
								width: 200,
								fontFamily: 'Inter-Regular',
							},
						},
						MenuListProps: {
							autoFocusItem: false,
							disableListWrap: true,
							paddingBottom: '8px',
						},
					}}
					renderValue={(selected) => (
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'nowrap',
								overflow: 'hidden',
								...(isDisabled && { cursor: TOPOLOGY_DISABLED_CURSOR }),
							}}
						>
							{isMultiSelect && Array.isArray(selected)
								? selected?.slice(0, 1).map((item) => (
										// <Chip
										// 	key={item?.site_id}
										// 	label={
										// 		item?.nested_site_name ||
										// 		item?.site_name
										// 	}
										// 	onDelete={(e) =>
										// 		handleDelete(item, e)
										// 	}
										// 	sx={{
										// 		fontFamily: 'Inter-Regular',
										// 		fontSize: '11px',
										// 		border: `1px solid ${color.background.brand}`,
										// 		backgroundColor:
										// 			color.background
										// 				.brand_outline,
										// 		color: color.text.primary,
										// 		borderRadius: '200px',
										// 		maxHeight: '18px',
										// 		display: 'flex',
										// 		flexDirection: 'row',
										// 		justifyContent: 'flex-start',
										// 		alignItems: 'center',
										// 		marginRight: '4px',
										// 	}}
										// 	onMouseDown={(e) =>
										// 		e.stopPropagation()
										// 	}
										// 	deleteIcon={
										// 		<PurpleCrossIcon
										// 			style={{
										// 				cursor: 'pointer',
										// 			}}
										// 		/>
										// 	}
										// />
										<Grid
											className="chip-name-item"
											key={item?.site_id}
											style={{
												border: `1px solid ${color.background.brand}`,
												backgroundColor: `${color.background.brand_outline}`,
											}}
										>
											<Label
												label={
													item?.nested_site_name ||
													item?.site_name
												}
												fontSize="xx_small"
												fontFamily="Inter-Regular"
												type="error"
												style={{
													color: color.text.primary,
													margin: '0 6px',
													maxWidth: '150px',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													// paddingLeft: '7px',
												}}
											/>
											<PurpleCrossIcon
												style={{
													marginRight: '6px',
													cursor: 'pointer',
												}}
												onMouseDown={(e) => {
													e.stopPropagation();
													e.preventDefault();
													// const newSelectedValues = selected.filter(
													// 	(value) => value !== item
													// );
													handleDelete(item, e);
												}}
											/>
										</Grid>
								  ))
								: selected && (
										<Label
											label={
												selected?.nested_site_name ||
												selected?.site_name
											}
											onMouseDown={(e) =>
												e.stopPropagation()
											}
											style={{
												color: isDisabled && 'grey',
											}}
											sx={{
												fontFamily: 'Inter-Regular',
												fontSize: '14px',
												backgroundColor:
													color.background
														.brand_outline,
												borderRadius: '200px',
												maxHeight: '18px',
												display: 'flex',
												flexDirection: 'row',
												justifyContent: 'flex-start',
												alignItems: 'center',
												marginRight: '4px',
												...(isDisabled && { cursor: TOPOLOGY_DISABLED_CURSOR }),
											}}
										/>
								  )}
							{isMultiSelect && selected?.length > 1 && (
								// <Chip
								// 	label={`+${selected.length - 1}`}
								// 	sx={{
								// 		fontFamily: 'Inter-Regular',
								// 		fontSize: '11px',
								// 		border: `1px solid ${color.background.brand}`,
								// 		backgroundColor:
								// 			color.background.primary,
								// 		color: color.text.primary,
								// 		borderRadius: '200px',
								// 		maxHeight: '18px',
								// 		display: 'flex',
								// 		flexDirection: 'row',
								// 		justifyContent: 'flex-start',
								// 		alignItems: 'center',
								// 		marginRight: '4px',
								// 	}}
								// 	style={{ paddingLeft: '0px' }}
								// />
								<Grid
									className="chip-name-item"
									style={{
										border: `1px solid ${color.background.brand}`,
										backgroundColor: `${color.background.primary}`,
									}}
								>
									<Label
										label={`+${selected.length - 1}`}
										fontSize="xx_small"
										fontFamily="Inter-Regular"
										type="error"
										style={{
											color: color.text.primary,
											margin: '0 6px',
										}}
									/>
								</Grid>
							)}
						</Box>
					)}
					sx={{
						fontSize: '14px',
						backgroundColor: 'none',
						...(isDisabled && {
							cursor: TOPOLOGY_DISABLED_CURSOR,
							'& .MuiSelect-select': { cursor: TOPOLOGY_DISABLED_CURSOR },
						}),
						...(readOnly && {
							pointerEvents: 'none',
							'& .MuiInput-underline:after': {
								borderBottom: 'none', // Prevents the focus effect (purple underline)
							},
						}),
					}}
				>
					<Box
						sx={{
							backgroundColor: color.background.primary,
							position: 'sticky',
							top: 0,
							zIndex: 1,
						}}
						onKeyDown={(e) => e.stopPropagation()}
					>
						<InputBase
							placeholder="Search"
							value={searchTerm}
							onChange={(e) => {
								e.preventDefault();
								const value = e.target.value;
								setSearchTerm(value);
							}}
							onKeyDown={(e) => e.stopPropagation()}
							onClick={(e) => e.stopPropagation()}
							style={{
								width: '100%',
								marginBottom: '8px',
								fontSize: FontFace.font_size.xx_inputlabel,
								padding: '8px 12px',
								fontFamily: 'Inter-Regular',
								lineHeight: '1.33',
								borderBottom: `1px solid ${color.input.line}`,
							}}
						/>
					</Box>
					{selectAllRequired && isMultiSelect && (
						<Box
							sx={{
								padding: showRootSitesOnly ? `${spacing.base_unit_2} ${spacing.base_unit_4}` : `${spacing.base_unit_none} ${spacing.base_unit_4}`,
								display: 'flex',
								flexDirection: 'column',
								gap: spacing.base_unit_1, // reduce vertical space between checkboxes
								alignItems: 'flex-start',
								position: 'sticky',
								top: '48px',
								background: color.background.primary,
								zIndex: 10,
								borderBottom: showRootSitesOnly ? `1px solid ${color.stroke.light}` : '',
								lineHeight: '1.33',
							}}
						>
							{showRootSitesOnly && (
									<Box sx={{ display: 'flex', alignItems: 'center',marginBottom: '12px' }}>
										<Checkbox
											checked={rootSitesOnly}
											onChecked={(val) => {
												// Call the click handler if provided, which will handle alerts
												if (onRootSitesOnlyClick) {
													onRootSitesOnlyClick(val, selectedSites, (shouldProceed) => {
														if (shouldProceed) {
															// Proceed with the change
															setRootSitesOnly(val);
															onRootSitesOnlyChange && onRootSitesOnlyChange(val);
														} else {
															// Cancel clicked: Don't change state, just leave checkbox as is
															// The checkbox state remains unchanged
														}
													});
												} else {
													// Default behavior if no click handler
													setRootSitesOnly(val);
													onRootSitesOnlyChange && onRootSitesOnlyChange(val);
												}
											}}
											sx={{ marginRight: '4px' }}
										/>									
										<Typography
											sx={{
												fontSize: FontFace.font_size.xx_inputlabel ,
												color: color.text.primary,
												fontFamily: 'Inter-Regular',
											}}
										>
											Auto-select Child Sites
										</Typography>
									</Box>
							)}
							{filteredSites?.length > 0 ? (
								<Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
									<Checkbox
										checked={
											selectedSites?.length ===
											filteredSites?.length
										}
										indeterminate={
											selectedSites?.length > 0 &&
											selectedSites?.length <
												filteredSites?.length
										}
										onChecked={(val) => handleSelectAll(val)}
										sx={{ marginRight: '4px' }}
									/>
									<Typography
										sx={{
											fontSize: FontFace.font_size.xx_inputlabel ,
											color: color.text.primary,
											fontFamily: 'Inter-Regular',
										}}
									>
										Select All ({filteredSites?.length})
									</Typography>
								</Box>
							) : (
								<Box sx={{ display: 'flex', alignItems: 'center' }}></Box>
							)}
						</Box>
					)}
					{filteredSites?.length === 0 && !liteLoading && (
						<Typography
							sx={{
								fontSize: FontFace.font_size.input_text,
								color: color.text.primary,
								fontFamily: 'Inter-Regular',
								padding: '4px',
								alignItems: 'center',
								textAlign: 'center',
							}}
						>
							No Option Available
						</Typography>
					)}
					{/* {filteredSites?.map((site) => (
						<MenuItem
							key={site?.site_id}
							value={site}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-start',
								width: '280px',
								maxWidth: '280px',
								height: '33px',
								fontSize: FontFace.font_size.input_text,
								color: color.text.primary,
								fontFamily: 'Inter-Regular',
								padding: spacing.base_unit_4,
								backgroundColor: isMultiSelect
									? selectedItems?.some(
											(selectedSite) =>
												selectedSite?.site_id ===
												site?.site_id
									  )
										? color.background.brand_outline
										: color.background.primary
									: selectedItems?.site_id === site?.site_id
									? color.background.brand_outline
									: color.background.primary,
							}}
						>
							{isMultiSelect ? (
								<Checkbox
									dataTestId="multipleSelect-Checkbox"
									sx={{ marginRight: '4px' }}
									checked={
										isMultiSelect &&
										selectedSites?.some(
											(selectedSite) =>
												selectedSite?.site_id ===
												site?.site_id
										)
									}
									onChecked={(checked) => {
										const updatedValues = checked
											? [...selectedItems, site]
											: selectedItems?.filter(
													(value) =>
														value?.site_id !==
														site?.site_id
											  );
										setSelectedSites(updatedValues);
										if (onChangeSelection) {
											onChangeSelection(updatedValues);
										}
									}}
								/>
							) : (
								<div style={{ width: '6px' }}></div>
							)}
							<Tooltip
								title={
									site?.nested_site_name || site?.site_name
								}
								PopperProps={{ disablePortal: true }}
								componentsProps={{
									tooltip: {
										sx: {
											bottom: '-4px',
											bgcolor: '#ffffff',
											color: '#000000',
											border: '1px solid rgba(0, 0, 0, 0.5)',
											borderRadius: '4px',
											padding: '8px',
											boxShadow:
												'0 4px 8px 0 rgba(0, 0, 0, 0.16)',
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'center',
											alignItems: 'center',
										},
									},
								}}
							>
								<span
									style={{
										flex: 1,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{site?.nested_site_name || site?.site_name}
								</span>
							</Tooltip>
						</MenuItem>
					))} */}
					{liteLoading && (
						<Box textAlign="center" padding={2}>
							<Loader />
						</Box>
					)}
					<FixedSizeList
						className="list-container"
						height={ROW_HEIGHT * MAX_VISIBLE_ITEMS}
						itemCount={filteredSites.length}
						itemSize={ROW_HEIGHT}
					>
						{renderRow}
					</FixedSizeList>
				</Select>
				{error && (
					<FormHelperText
						sx={{
							fontSize: '12px',
							color: color.text.error,
							fontFamily: 'Inter-Regular',
							marginTop: '4px',
						}}
					>
						{error}
					</FormHelperText>
				)}
			</FormControl>
		</div>
	);
};

TopologySelectWOLazyLoading.propTypes = {
	limit: PropTypes.number,
	isMultiSelect: PropTypes.bool,
	onChangeSelection: PropTypes.func,
	label: PropTypes.string,
	width: PropTypes.number,
	sx: PropTypes.object,
	selectedItems: PropTypes.array,
	selectAllRequired: PropTypes.bool,
	doesSelectRoot: PropTypes.bool,
	isSelectAll: PropTypes.bool,
	error: PropTypes.string,
	required: PropTypes.bool,
	isDisabled: PropTypes.bool,
	selectAllDb: PropTypes.bool,
	onSelectAllDb: PropTypes.object,
	readOnly: PropTypes.bool,
	isTipSitesListed: PropTypes.bool,
	isTip: PropTypes.bool,
	setSlctAll: PropTypes.func,
	excludeSiteIds: PropTypes.arrayOf(
	PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	),
	showRootSitesOnly: PropTypes.bool,
	rootSitesOnlyValue: PropTypes.bool,
	onRootSitesOnlyChange: PropTypes.func,
	onRootSitesOnlyClick: PropTypes.func,
	onCloseDropdown: PropTypes.func,
};

export default TopologySelectWOLazyLoading;
